import { useState, useEffect } from "react";
import axios from "axios";
import DemoLayout from "@/components/demo/DemoLayout";
import { useTheme } from "@/contexts/ThemeContext";
import { Lock, RefreshCw, FileCode2, Monitor, CheckCircle, Clock, AlertTriangle, Award, Shield } from "lucide-react";

const API_EXEC = `${process.env.REACT_APP_BACKEND_URL}/api/executive`;
const mono = { fontFamily: "var(--font-mono)" };

/* ── Demo accounts where individual scores are hidden until the
   assessment cycle is complete (Sagicor stakeholders). ── */
const SCORE_GATED_EMAILS = ["neil_freyke@sagicor.com", "david_noel@sagicor.com"];
function isScoreGatedUser() {
  try {
    const raw = localStorage.getItem("userInfo");
    if (!raw) return false;
    const info = JSON.parse(raw);
    const email = (info?.email || "").toLowerCase().trim();
    return SCORE_GATED_EMAILS.includes(email);
  } catch {
    return false;
  }
}

/* ── theme-aware style tokens (same as Command Center) ── */
const crd = {
  background: "var(--surface-card)",
  border: "var(--border-width) solid var(--border-default)",
  borderRadius: "1rem",
  boxShadow: "var(--shadow-card)",
  transition: "background 150ms ease, border-color 150ms ease",
};
const inner = { background: "var(--surface-inner)", borderRadius: "0.5rem" };
const track = { background: "var(--surface-muted)", borderRadius: "9999px" };
const tp = { color: "var(--text-primary)" };
const ts = { color: "var(--text-secondary)" };
const tm = { color: "var(--text-muted)" };
const bdr = { borderColor: "var(--border-default)" };
const labelS = { ...tm, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 };

/* ═══ METRIC CARD ═══ */
function MetricCard({ icon: Icon, value, label, sub, accent, testId }) {
  return (
    <div style={crd} className="p-5 flex flex-col items-center justify-center text-center" data-testid={testId}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3"
        style={{ background: accent ? `${accent}15` : "var(--surface-inner)" }}>
        <Icon className="w-5 h-5" style={{ color: accent || "var(--text-muted)" }} />
      </div>
      <p className={`text-2xl font-semibold tracking-tight`}
        style={{ ...mono, color: accent || "var(--text-primary)" }}>{value}</p>
      <p className="mt-2" style={labelS}>{label}</p>
      {sub && <p className="text-[10px] mt-0.5" style={tm}>{sub}</p>}
    </div>
  );
}

/* ═══ PROGRESS ROW ═══ */
function ProgressRow({ label, completed, total, color }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1.5">
        <span className="text-xs" style={tp}>{label}</span>
        <span className="text-sm font-semibold" style={{ ...mono, color }}>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={track}>
        <div className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${pct}%`, background: `linear-gradient(to right, ${color}, ${color}cc)` }} />
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[10px]" style={tm}>{completed} completed</span>
        <span className="text-[10px]" style={tm}>{total} total</span>
      </div>
    </div>
  );
}

/* ═══ STATUS BREAKDOWN ═══ */
function StatusBreakdown({ icon: Icon, title, color, data, testId }) {
  return (
    <div style={crd} className="p-6" data-testid={testId}>
      <div className="flex items-center gap-2 mb-5">
        <Icon className="w-5 h-5" style={{ color }} />
        <p className="text-sm font-semibold" style={tp}>{title}</p>
        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold ml-auto"
          style={{ color, background: `${color}15` }}>
          {data.total} candidates
        </span>
      </div>
      <ProgressRow label="Completion" completed={data.completed} total={data.total} color={color} />
      <div className="grid grid-cols-3 gap-3 mt-4">
        {[
          { label: "Done", value: data.completed, color: "#10B981" },
          { label: "In Progress", value: data.in_progress, color: "#F59E0B" },
          { label: "Not Started", value: data.not_started, color: "var(--text-muted)" },
        ].map((s) => (
          <div key={s.label} className="p-3 rounded-lg text-center" style={inner}>
            <p className="text-lg font-semibold" style={{ ...mono, color: s.color }}>{s.value}</p>
            <p className="text-[10px] mt-0.5" style={labelS}>{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══ JOB FAMILY TABLE ═══ */
function JobFamilyBreakdown({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="p-6" style={crd}>
        <p className="text-sm font-semibold mb-5" style={tp}>Progress by Job Family</p>
        <div className="flex flex-col items-center py-8">
          <p className="text-xs" style={tm}>No job family data yet</p>
        </div>
      </div>
    );
  }
  return (
    <div className="p-6" style={crd} data-testid="ta-job-family-breakdown">
      <p className="text-sm font-semibold mb-5" style={tp}>Progress by Job Family</p>
      <div className="space-y-3">
        {data.map((jf) => {
          const pct = jf.total > 0 ? Math.round((jf.completed / jf.total) * 100) : 0;
          return (
            <div key={jf.job_family}>
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-xs" style={tp}>{jf.job_family}</span>
                <div className="flex items-center gap-3">
                  <span className="text-[10px]" style={{ ...tm, ...mono }}>{jf.completed}/{jf.total}</span>
                  <span className="text-xs font-semibold" style={{ ...mono, color: pct >= 75 ? "#10B981" : pct >= 40 ? "#F59E0B" : "var(--text-secondary)" }}>
                    {pct}%
                  </span>
                </div>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={track}>
                <div className="h-full rounded-full bg-gradient-to-r from-[#00AEEF] to-[#00AEEF]/70"
                  style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 pt-3 mt-3 border-t" style={bdr}>
        {[
          { label: "Completed", color: "#10B981" },
          { label: "In Progress", color: "#F59E0B" },
          { label: "Not Started", color: "var(--text-disabled)" },
        ].map((l) => (
          <span key={l.label} className="flex items-center gap-1.5 text-[10px]" style={tm}>
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: l.color }} />
            {l.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ═══ REGIONAL TABLE ═══ */
function RegionalDeployment({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="p-6" style={crd}>
        <p className="text-sm font-semibold mb-5" style={tp}>Regional Deployment</p>
        <div className="flex flex-col items-center py-8">
          <p className="text-xs" style={tm}>No regional data yet</p>
        </div>
      </div>
    );
  }
  return (
    <div className="p-6" style={crd} data-testid="ta-regional-deployment">
      <p className="text-sm font-semibold mb-5" style={tp}>Regional Deployment</p>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b" style={bdr}>
            <th className="text-left py-2 font-semibold" style={labelS}>Region</th>
            <th className="text-center py-2 font-semibold" style={labelS}>TA</th>
            <th className="text-center py-2 font-semibold" style={labelS}>HOS</th>
            <th className="text-center py-2 font-semibold" style={labelS}>Total</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r) => (
            <tr key={r.region} className="border-b last:border-0" style={bdr}>
              <td className="py-2.5 capitalize" style={tp}>{r.region?.replace(/_/g, " ") || "Unknown"}</td>
              <td className="py-2.5 text-center">
                <span style={{ ...mono, ...tp }}>{r.ta_completed}</span>
                <span style={tm}>/{r.ta_total}</span>
              </td>
              <td className="py-2.5 text-center">
                <span style={{ ...mono, ...tp }}>{r.hos_completed}</span>
                <span style={tm}>/{r.hos_total}</span>
              </td>
              <td className="py-2.5 text-center font-semibold" style={{ ...mono, ...tp }}>
                {r.ta_total + r.hos_total}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ═══ RECENT SCORES (gated placeholder for Sagicor execs) ═══ */
function PendingScoresPlaceholder() {
  return (
    <div className="p-6" style={crd} data-testid="ta-recent-scores-pending">
      <div className="flex items-center gap-2 mb-5">
        <Award className="w-5 h-5" style={{ color: "#8B5CF6" }} />
        <p className="text-sm font-semibold" style={tp}>Recent Scoring Results</p>
      </div>
      <div className="p-8 flex flex-col items-center text-center" style={inner}>
        <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
          style={{ background: "rgba(139,92,246,0.12)" }}>
          <Lock className="w-5 h-5" style={{ color: "#8B5CF6" }} />
        </div>
        <p className="text-sm font-semibold mb-1" style={tp}>
          Scores will be revealed after the assessment is complete
        </p>
        <p className="text-xs max-w-md" style={ts}>
          Individual candidate scores remain confidential while the assessment cycle is in progress.
          Final results will appear here once all candidates have completed their TA &amp; HOS.
        </p>
      </div>
    </div>
  );
}

/* ═══ RECENT SCORES ═══ */
function RecentScores({ data }) {
  if (!data || data.length === 0) return null;
  const scoreBadge = (score) => {
    if (score >= 80) return { bg: "rgba(16,185,129,0.12)", color: "#10B981" };
    if (score >= 60) return { bg: "rgba(245,158,11,0.12)", color: "#F59E0B" };
    return { bg: "rgba(239,68,68,0.12)", color: "#EF4444" };
  };
  return (
    <div className="p-6" style={crd} data-testid="ta-recent-scores">
      <div className="flex items-center gap-2 mb-5">
        <Award className="w-5 h-5" style={{ color: "#8B5CF6" }} />
        <p className="text-sm font-semibold" style={tp}>Recent Scoring Results</p>
      </div>
      <div className="space-y-2">
        {data.map((s, i) => {
          const sb = scoreBadge(s.composite_score);
          return (
            <div key={i} className="p-3 flex items-center justify-between" style={inner}>
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium truncate max-w-[160px]" style={tp}>{s.candidate_name}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                  style={{
                    color: s.assessment_type === "technical_assessment" ? "#00AEEF" : "#8B5CF6",
                    background: s.assessment_type === "technical_assessment" ? "rgba(0,174,239,0.1)" : "rgba(139,92,246,0.1)",
                  }}>
                  {s.assessment_type === "technical_assessment" ? "TA" : "HOS"}
                </span>
                <span className="text-[10px]" style={tm}>{s.job_family}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs px-2.5 py-1 rounded-md font-bold"
                  style={{ ...mono, color: sb.color, background: sb.bg }}>
                  {s.composite_score}
                </span>
                <span className="text-[10px]" style={tm}>
                  {s.scored_at ? new Date(s.scored_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══ EMPTY STATE ═══ */
function EmptyState({ rubricCount }) {
  return (
    <div className="p-8 rounded-2xl flex flex-col items-center text-center" style={crd} data-testid="ta-empty-state">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
        style={{ background: "var(--color-accent-bg)" }}>
        <FileCode2 className="w-8 h-8" style={{ color: "var(--color-accent)" }} />
      </div>
      <p className="text-base font-semibold mb-2" style={tp}>No Assessments Uploaded Yet</p>
      <p className="text-xs max-w-md mb-5" style={ts}>
        Technical Assessment and Hands-On Simulation candidates will appear here once they are uploaded via the Admin dashboard.
      </p>
      <div className="flex gap-3">
        <span className="text-[10px] px-3 py-1.5 rounded-full font-semibold"
          style={{ color: "var(--color-accent)", background: "var(--color-accent-bg)" }}>
          {rubricCount} Active Rubrics
        </span>
        <span className="text-[10px] px-3 py-1.5 rounded-full font-semibold"
          style={{ color: "#10B981", background: "rgba(16,185,129,0.1)" }}>
          AI Scoring Ready
        </span>
      </div>
    </div>
  );
}

/* ═══ MAIN PAGE ═══ */
export default function TechnicalAssessmentPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isDark } = useTheme();

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("executiveToken") || localStorage.getItem("authToken");
      const res = await axios.get(`${API_EXEC}/ta-hos-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      console.error("TA/HOS stats error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <DemoLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-sm" style={ts}>Loading Technical Assessment data...</div>
        </div>
      </DemoLayout>
    );
  }

  const ta = data?.technical_assessment || { total: 0, completed: 0, in_progress: 0, not_started: 0 };
  const hos = data?.hands_on_simulation || { total: 0, completed: 0, in_progress: 0, not_started: 0 };
  const scoring = data?.scoring || { total_scored: 0, avg_composite_score: 0, active_rubrics: 0 };
  const integrity = data?.integrity || { total_events: 0 };
  const grandTotal = ta.total + hos.total;
  const grandCompleted = ta.completed + hos.completed;
  const overallRate = grandTotal > 0 ? Math.round((grandCompleted / grandTotal) * 100) : 0;
  const isEmpty = grandTotal === 0;

  return (
    <DemoLayout>
      <div className="space-y-4" data-testid="ta-hos-page">
        {/* Header */}
        <div className="flex justify-between items-start mb-4" data-testid="ta-header">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight" style={tp}>Technical Assessment</h1>
            <p className="text-sm mt-1" style={ts}>TA & Hands-On Simulation progress across the organization</p>
          </div>
          <button onClick={fetchData}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-colors"
            style={{ background: "var(--surface-muted)", color: "var(--text-secondary)" }}
            data-testid="ta-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>

        {isEmpty ? (
          <EmptyState rubricCount={scoring.active_rubrics} />
        ) : (
          <>
            {/* Row 1: Top-level metrics */}
            <div className="grid grid-cols-5 gap-4">
              <MetricCard icon={FileCode2} value={grandTotal} label="Total Candidates"
                sub={`TA: ${ta.total} | HOS: ${hos.total}`} accent="#00AEEF" testId="metric-total" />
              <MetricCard icon={CheckCircle} value={grandCompleted} label="Completed"
                sub={`${overallRate}% rate`} accent="#10B981" testId="metric-completed" />
              <MetricCard icon={Clock} value={ta.in_progress + hos.in_progress} label="In Progress"
                sub="Active now" accent="#F59E0B" testId="metric-in-progress" />
              <MetricCard icon={Award} value={scoring.total_scored} label="Scored"
                sub={scoring.avg_composite_score > 0 ? `Avg: ${scoring.avg_composite_score}/100` : "Awaiting scores"}
                accent="#8B5CF6" testId="metric-scored" />
              <MetricCard icon={Shield} value={integrity.total_events} label="Integrity Events"
                sub={`${scoring.active_rubrics} rubrics active`} accent="#EF4444" testId="metric-integrity" />
            </div>

            {/* Row 2: TA + HOS Breakdown side by side */}
            <div className="grid grid-cols-2 gap-4">
              <StatusBreakdown icon={FileCode2} title="Technical Assessment" color="#00AEEF"
                data={ta} testId="breakdown-ta" />
              <StatusBreakdown icon={Monitor} title="Hands-On Simulation" color="#8B5CF6"
                data={hos} testId="breakdown-hos" />
            </div>

            {/* Row 3: Job Family + Regional */}
            <div className="grid grid-cols-5 gap-4">
              <div className="col-span-3">
                <JobFamilyBreakdown data={data?.by_job_family} />
              </div>
              <div className="col-span-2">
                <RegionalDeployment data={data?.by_region} />
              </div>
            </div>

            {/* Row 4: Recent Scores (gated for Neil & David) */}
            {isScoreGatedUser()
              ? <PendingScoresPlaceholder />
              : <RecentScores data={data?.recent_scores} />}
          </>
        )}
      </div>
    </DemoLayout>
  );
}
